import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { parseZonedDateTime } from '@rematter/common/dateTime';
import { SalesOrderStatus } from '@rematter/frontend-lib-api-client';

import { SalesOrderRecord } from '../types/salesOrder';
import { toMoney } from '../utils/money';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const salesOrderDetailQueryAtom = createQueryAtomFactory(
  'trade.salesOrderDetail',
  (params: { id: string }) => ({
    salesOrders: {
      __args: {
        filter: {
          ids: [params.id],
          includeLineItems: true,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        type: true,
        status: true,
        startDate: true,
        endDate: true,
        counterpartyReferenceNumber: true,
        shippingAddress: {
          addressType: true,
          streetAddress: true,
          city: true,
          state: true,
          postalCode: true,
          country: true,
        },
        billingAddress: {
          addressType: true,
          streetAddress: true,
          city: true,
          state: true,
          postalCode: true,
          country: true,
        },
        paymentCurrency: true,
        freightTerm: true,
        paymentTerm: true,
        doNotShip: true,
        recyclerExternalNotes: true,
        recyclerInternalNotes: true,
        completionNotes: true,
        customAttributes: true,
        weightDisplayUnit: true,
        lineItems: {
          id: true,
          unit: true,
          quantity: true,
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          totalPrice: {
            amount: true,
            currencyCode: true,
          },
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
          item: {
            id: true,
            name: true,
            description: true,
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
        account: {
          id: true,
          name: true,
          customAttributes: true,
        },
        contact: {
          id: true,
          fullName: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
        accountRepresentative: {
          id: true,
          firstName: true,
          lastName: true,
        },
        bookings: {
          id: true,
          documentNumber: true,
        },
      },
    },
  }),
  {
    select: (data): SalesOrderRecord => {
      const salesOrder = data.salesOrders.entries[0];
      return {
        ...salesOrder,
        status: salesOrder.status as SalesOrderStatus,
        startDate: maybeParseCalendarDate(salesOrder.startDate),
        endDate: maybeParseCalendarDate(salesOrder.endDate),
        timestamps: {
          createdAt: parseZonedDateTime(salesOrder.timestamps.createdAt),
          updatedAt: parseZonedDateTime(salesOrder.timestamps.updatedAt),
        },
        bookings: salesOrder.bookings?.map((b) => ({
          id: b.id,
          bookingNumber: b.documentNumber,
        })),
        lineItems: salesOrder.lineItems?.map((lineItem) => ({
          ...lineItem,
          unitPrice: toMoney(lineItem.unitPrice),
          totalPrice: toMoney(lineItem.totalPrice),
          itemId: lineItem.item?.id ?? null,
        })),
        weightDisplayUnit: salesOrder.weightDisplayUnit,
      };
    },
  },
);
